import { DEMO_RANKING_LISTS, DEMO_TODAY, type IRankingList } from '../fixtures';
import type { DemoAction } from './actions';

/**
 * Act 4, ranking operations: the ranking lists with every change made to them. The worklists, the
 * firm exchange and the reports went live with SR-230 (Lists, Import, Reports), the DD calls with
 * SR-314 and the updated metrics with SR-315, and their mocks were deleted with their slices; the
 * initial review keeps a state of its own (`initial-review.ts`).
 *
 * **A ranking list is an order, and a place is a position in it.** Nothing stores a place: remove
 * an advisor and everyone below moves up one; insert one at place N and everyone from N moves down
 * one (RNK-14, WF-12 steps 4–7). Each change is logged with its optional note. Publishing freezes
 * the lists, and no change is taken after it — it writes nothing onto a record since the worklists'
 * pane that read it went.
 */

export interface IRankingChange {
  listId: string;
  kind: 'removed' | 'inserted';
  personId: string;
  place: number;
  note: string;
  by: string;
  at: string;
}

export interface IAct4State {
  rankings: {
    lists: IRankingList[];
    changes: IRankingChange[];
    finalistsSent: Record<string, string>;
    published: Nullable<{ at: string; by: string }>;
  };
}

export function initialAct4(): IAct4State {
  return {
    rankings: {
      lists: DEMO_RANKING_LISTS.map((list) => ({ ...list, order: [...list.order] })),
      changes: [],
      finalistsSent: {},
      published: null
    }
  };
}

function rankingsReducer(rankings: IAct4State['rankings'], action: DemoAction): IAct4State['rankings'] {
  if (rankings.published && action.type.startsWith('ranking/')) return rankings;
  switch (action.type) {
    case 'ranking/remove': {
      const list = rankings.lists.find((entry) => entry.id === action.listId);
      const place = list ? list.order.indexOf(action.personId) + 1 : 0;
      if (!list || place === 0) return rankings;
      return {
        ...rankings,
        lists: rankings.lists.map((entry) =>
          entry.id === list.id ? { ...entry, order: entry.order.filter((id) => id !== action.personId) } : entry
        ),
        changes: [
          ...rankings.changes,
          { listId: list.id, kind: 'removed', personId: action.personId, place, note: action.note, by: action.by, at: DEMO_TODAY }
        ]
      };
    }
    case 'ranking/insert': {
      const list = rankings.lists.find((entry) => entry.id === action.listId);
      if (!list || list.order.includes(action.personId)) return rankings;
      const index = Math.min(Math.max(action.place, 1), list.order.length + 1) - 1;
      const order = [...list.order];
      order.splice(index, 0, action.personId);
      return {
        ...rankings,
        lists: rankings.lists.map((entry) => (entry.id === list.id ? { ...entry, order } : entry)),
        changes: [
          ...rankings.changes,
          { listId: list.id, kind: 'inserted', personId: action.personId, place: index + 1, note: action.note, by: action.by, at: DEMO_TODAY }
        ]
      };
    }
    case 'ranking/send-finalists':
      return {
        ...rankings,
        finalistsSent: {
          ...rankings.finalistsSent,
          ...Object.fromEntries(action.firmIds.map((id) => [id, DEMO_TODAY]))
        }
      };
    case 'ranking/publish':
      return { ...rankings, published: { at: DEMO_TODAY, by: action.by } };
    default:
      return rankings;
  }
}

export function act4Reducer(state: IAct4State, action: DemoAction): IAct4State {
  const rankings = rankingsReducer(state.rankings, action);
  return rankings === state.rankings ? state : { ...state, rankings };
}
